import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CampoDeTexto } from "@base/ui";
import { Button, Modal } from "@base/ui/renke";
import { ErroDaApi } from "../../../shared/http/erro-da-api";
import { useProjetoAtivo } from "../../../shared/tenant/projeto.contexto";
import { useClientesGateway } from "../application/clientes.contexto";

/**
 * Modal de criacao de workspace (= cliente) com contexto editorial.
 */
export function ModalNovoWorkspace() {
  const { criacaoAberta, fecharCriacao, definirProjetoAtivo, recarregarLista } =
    useProjetoAtivo();
  const gateway = useClientesGateway();
  const queryClient = useQueryClient();

  const [nome, definirNome] = useState("");
  const [tomDeVoz, definirTomDeVoz] = useState("");
  const [posicionamento, definirPosicionamento] = useState("");
  const [maisAberto, definirMaisAberto] = useState(false);
  const [preferenciasVisuais, definirPreferenciasVisuais] = useState("");
  const [volumetriaPadrao, definirVolumetriaPadrao] = useState("");
  const [datasComemorativas, definirDatasComemorativas] = useState("");
  const [notas, definirNotas] = useState("");
  const [erro, definirErro] = useState<string | null>(null);

  const criar = useMutation({
    mutationFn: () =>
      gateway.criar({
        nome: nome.trim(),
        tomDeVoz: tomDeVoz.trim() || null,
        posicionamento: posicionamento.trim() || null,
        preferenciasVisuais: preferenciasVisuais.trim() || null,
        volumetriaPadrao: volumetriaPadrao.trim() || null,
        datasComemorativas: datasComemorativas.trim() || null,
        notas: notas.trim() || null,
      }),
    onSuccess: async (cliente) => {
      await recarregarLista();
      await definirProjetoAtivo(cliente.id);
      await queryClient.invalidateQueries({ queryKey: ["clientes"] });
      resetar();
      fecharCriacao();
    },
    onError: (causa) => {
      definirErro(
        causa instanceof ErroDaApi
          ? causa.message
          : causa instanceof Error
            ? causa.message
            : "Não foi possível criar o workspace.",
      );
    },
  });

  function resetar() {
    definirNome("");
    definirTomDeVoz("");
    definirPosicionamento("");
    definirPreferenciasVisuais("");
    definirVolumetriaPadrao("");
    definirDatasComemorativas("");
    definirNotas("");
    definirMaisAberto(false);
    definirErro(null);
  }

  function aoFechar() {
    if (criar.isPending) return;
    resetar();
    fecharCriacao();
  }

  function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    definirErro(null);
    if (nome.trim().length < 2) {
      definirErro("Informe o nome do cliente (mínimo 2 caracteres).");
      return;
    }
    criar.mutate();
  }

  return (
    <Modal
      aberto={criacaoAberta}
      titulo="Novo workspace"
      descricao="Cada workspace é um cliente. Preencha o contexto que a equipe e a IA usam no dia a dia."
      onFechar={aoFechar}
      mostrarFechar={false}
      className="max-w-lg"
      rodape={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variante="secondary"
            tamanho="sm"
            onClick={aoFechar}
            disabled={criar.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            form="form-novo-workspace"
            variante="brand"
            tamanho="sm"
            carregando={criar.isPending}
            disabled={criar.isPending || nome.trim().length < 2}
          >
            Criar workspace
          </Button>
        </div>
      }
    >
      <form
        id="form-novo-workspace"
        className="mt-4 space-y-4"
        onSubmit={aoEnviar}
      >
        <CampoDeTexto
          rotulo="Nome do cliente"
          value={nome}
          onChange={(e) => definirNome(e.target.value)}
          placeholder="Ex.: Clínica Horizonte"
          required
          autoFocus
        />
        <CampoDeTexto
          rotulo="Tom de voz"
          value={tomDeVoz}
          onChange={(e) => definirTomDeVoz(e.target.value)}
          placeholder="Ex.: acolhedor, técnico, direto"
        />
        <CampoDeTexto
          rotulo="Posicionamento"
          value={posicionamento}
          onChange={(e) => definirPosicionamento(e.target.value)}
          placeholder="Como o cliente quer ser visto"
        />

        <button
          type="button"
          className="cursor-pointer text-sm font-medium text-muted-foreground underline-offset-4 transition-colors duration-150 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => definirMaisAberto((v) => !v)}
          aria-expanded={maisAberto}
        >
          {maisAberto ? "Menos contexto" : "Mais contexto"}
        </button>

        {maisAberto ? (
          <div className="space-y-4 animate-aparecer">
            <CampoDeTexto
              rotulo="Preferências visuais"
              value={preferenciasVisuais}
              onChange={(e) => definirPreferenciasVisuais(e.target.value)}
              placeholder="Cores, referências, o que evitar"
            />
            <CampoDeTexto
              rotulo="Volumetria padrão"
              value={volumetriaPadrao}
              onChange={(e) => definirVolumetriaPadrao(e.target.value)}
              placeholder="Ex.: 8 reels, 4 carrosséis / mês"
            />
            <CampoDeTexto
              rotulo="Datas comemorativas"
              value={datasComemorativas}
              onChange={(e) => definirDatasComemorativas(e.target.value)}
              placeholder="Datas que pedem conteúdo"
            />
            <CampoDeTexto
              rotulo="Notas"
              value={notas}
              onChange={(e) => definirNotas(e.target.value)}
              placeholder="Observações da equipe"
            />
          </div>
        ) : null}

        {erro ? (
          <p className="text-sm text-destructive" role="alert">
            {erro}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
